// ═══════════════════════════════════════════════════════════════════════════════
// Employer — Edit all tasks and task groups with lifecycle filters
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { EmployerTaskSubPageHeader } from '../../components/task/employer/EmployerTaskSubPageHeader';
import { StatusFilterChipRow } from '../../components/ui/StatusFilterChipRow';
import { RecurrenceBadge } from '../../components/task';
import { CornerStatusRibbon } from '../../components/ui/CornerStatusRibbon';
import { ListRowChevron } from '../../components/ui/ListRowChevron';
import {
  listCornerStatusCardInnerShellClass,
  taskTemplateLifecycleRibbonClass,
} from '../../utils/listCornerStatusRibbon';
import { SCROLL_BOTTOM_FOR_FIXED_ACTION_ROW } from '../../lib/layoutChrome';
import { membershipApi, taskApi, taskGroupApi } from '../../lib/api';
import { navigateBack } from '../../lib/navigateBack';
import { getDateInTimezone } from '../../utils/employerTaskDayGrouping';
import { resolveDisplayTimeZone } from '../../lib/userTodayYmd';
import { useEmployerEditTaskNavigation } from '../../hooks/useEmployerEditTaskNavigation';
import { useToast } from '../../components/ui/ToastProvider';
import {
  RECURRENCE_TYPE,
  TASK_GROUP_CONFIG_TYPE,
  TASK_KIND,
  USER_LEVEL,
  USER_ROLE,
  type TaskGroupListItem,
  type TaskListItem,
} from '@fdh/shared';

type EditSubTabType = 'all' | 'effective' | 'expired';
type TaskDisplayLifecycle = 'not_started' | 'effective' | 'expired';

const isEditSubTabType = (value: string | null): value is EditSubTabType =>
  value === 'all' || value === 'effective' || value === 'expired';

const getTaskDisplayLifecycle = (
  task: TaskListItem,
  todayYmd: string,
): TaskDisplayLifecycle => {
  if (task.status === 'archived') {
    return 'expired';
  }

  const isOneTime = task.recurrence === RECURRENCE_TYPE.NONE;
  if (isOneTime) {
    if (task.occurrenceDate < todayYmd) {
      return 'expired';
    }
    return 'effective';
  }

  if (task.startDate === '' || task.startDate > todayYmd) {
    return 'not_started';
  }
  if (task.endDate !== '' && task.endDate < todayYmd) {
    return 'expired';
  }
  return 'effective';
};

const resolveEditAllTasksTitle = (
  t: (key: string, params?: Record<string, string | number>) => string,
): string => {
  const primary = t('task.list.tabEditAllTasks');
  if (primary !== 'task.list.tabEditAllTasks') {
    return primary;
  }
  const fallback = t('task.list.menuEditAllTasks');
  if (fallback !== 'task.list.menuEditAllTasks') {
    return fallback;
  }
  return t('task.list.tabEditTask');
};

export const EmployerEditAllTasksPage = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { showErrorToast, showSuccessToast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { handleEditTaskClick, handleEditGroupClick } = useEmployerEditTaskNavigation();

  const isL3Employer = user?.role === USER_ROLE.EMPLOYER && user.level === USER_LEVEL.PAID;

  const timezone = resolveDisplayTimeZone(user?.settings.timezone);
  const todayYmd = useMemo(() => getDateInTimezone(timezone), [timezone]);

  const editSubTabQueryValue = searchParams.get('editSubTab');
  const editSubTab: EditSubTabType = isEditSubTabType(editSubTabQueryValue) ? editSubTabQueryValue : 'all';

  const [tasks, setTasks] = useState<TaskListItem[]>([]);
  const [taskGroups, setTaskGroups] = useState<TaskGroupListItem[]>([]);
  const [restorableTaskIdsByKey, setRestorableTaskIdsByKey] = useState<Map<string, string[]>>(new Map());
  const [restoreBusyKey, setRestoreBusyKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const pageTitle = useMemo(() => resolveEditAllTasksTitle(t), [t]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (!isEditSubTabType(next.get('editSubTab'))) {
      next.set('editSubTab', 'all');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [taskResponse, groupResponse] = await Promise.all([
        taskApi.list('all'),
        taskGroupApi.list(),
      ]);

      if (!taskResponse.success) {
        setError(taskResponse.error ?? t('task.list.loadError'));
        return;
      }

      const d = taskResponse.data;
      const list: TaskListItem[] = Array.isArray(d) ? d : (d?.items ?? []);
      setTasks(list);
      if (groupResponse.success && groupResponse.data?.groups) {
        setTaskGroups(groupResponse.data.groups);
      } else {
        setTaskGroups([]);
      }
    } catch {
      setError(t('task.list.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void fetchTasks();
  }, [fetchTasks]);

  const loadRestorableItems = useCallback(async () => {
    if (!isL3Employer) {
      setRestorableTaskIdsByKey(new Map());
      return;
    }
    try {
      const res = await membershipApi.listRestorableArchivedTasks();
      if (!res.success || !res.data?.items) {
        setRestorableTaskIdsByKey(new Map());
        return;
      }
      const next = new Map<string, string[]>();
      res.data.items.forEach((item) => {
        next.set(item.itemKey, item.taskIds);
      });
      setRestorableTaskIdsByKey(next);
    } catch {
      setRestorableTaskIdsByKey(new Map());
    }
  }, [isL3Employer]);

  useEffect(() => {
    void loadRestorableItems();
  }, [loadRestorableItems]);

  const handleRestoreArchivedItem = async (itemKey: string): Promise<void> => {
    const taskIds = restorableTaskIdsByKey.get(itemKey);
    if (!taskIds || taskIds.length === 0) {
      return;
    }
    setRestoreBusyKey(itemKey);
    try {
      const res = await membershipApi.restoreArchivedTasks(taskIds);
      if (!res.success) {
        showErrorToast(res.error ?? t('membership.restoreModal.error'));
        return;
      }
      showSuccessToast(t('membership.restoreTask.success'));
      await Promise.all([fetchTasks(), loadRestorableItems()]);
    } catch {
      showErrorToast(t('membership.restoreModal.error'));
    } finally {
      setRestoreBusyKey(null);
    }
  };

  const editSubTabFilterOptions = useMemo(
    () =>
      (['all', 'effective', 'expired'] as EditSubTabType[]).map((subTab) => ({
        value: subTab,
        label:
          subTab === 'all'
            ? t('task.list.tabAll')
            : subTab === 'effective'
              ? t('task.list.tabEffectiveTask')
              : t('task.list.tabExpiredTask'),
      })),
    [t],
  );

  const filteredEditTasks = (tasks ?? []).filter((task) => {
    const lifecycle = getTaskDisplayLifecycle(task, todayYmd);
    if (editSubTab === 'effective') {
      return lifecycle === 'effective' || lifecycle === 'not_started';
    }
    if (editSubTab === 'expired') {
      return lifecycle === 'expired';
    }
    return true;
  });

  const filteredEditGroups = (taskGroups ?? []).filter((group) => {
    if (editSubTab === 'effective') {
      return group.lifecycle === 'effective' || group.lifecycle === 'not_started';
    }
    if (editSubTab === 'expired') {
      return group.lifecycle === 'expired';
    }
    return true;
  });

  const handleBack = () => {
    navigateBack(navigate, '/tasks');
  };

  const handleEditSubTabChange = (subTab: EditSubTabType): void => {
    const next = new URLSearchParams(searchParams);
    next.set('editSubTab', subTab);
    setSearchParams(next, { replace: true });
  };

  const isEmpty = filteredEditTasks.length === 0 && filteredEditGroups.length === 0;

  const resolveLifecycleStatusLabel = (lifecycle: TaskDisplayLifecycle): string => {
    if (lifecycle === 'not_started') {
      return t('task.list.statusNotStarted');
    }
    if (lifecycle === 'effective') {
      return t('task.list.statusEffective');
    }
    return t('task.list.statusExpired');
  };

  return (
    <div className="min-h-full bg-[#f7f8fb] dark:bg-gray-950">
      <EmployerTaskSubPageHeader title={pageTitle} onBack={handleBack} />

      <div className="mx-auto max-w-lg px-4 pt-4">
        <StatusFilterChipRow
          ariaLabel={t('task.list.editFiltersAria')}
          options={editSubTabFilterOptions}
          value={editSubTab}
          onChange={handleEditSubTabChange}
        />
      </div>

      <main className={`mx-auto max-w-lg px-4 py-4 ${SCROLL_BOTTOM_FOR_FIXED_ACTION_ROW}`}>
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
          </div>
        ) : error ? (
          <div className="rounded-xl bg-red-50 p-6 text-center dark:bg-red-950/40">
            <p className="text-red-600 dark:text-red-300" role="alert">
              {error}
            </p>
            <button
              type="button"
              onClick={() => void fetchTasks()}
              className="mt-3 text-sm font-medium text-red-700 underline hover:no-underline focus:outline-none dark:text-red-200"
            >
              {t('task.action.refresh')}
            </button>
          </div>
        ) : isEmpty ? (
          <div className="rounded-xl bg-white p-8 text-center shadow-sm dark:bg-gray-900">
            <p className="text-gray-600 dark:text-gray-300">{t('task.list.empty')}</p>
            <p className="mt-1 text-sm text-gray-400 dark:text-gray-500">{t('task.list.emptyHint')}</p>
          </div>
        ) : (
          <ul className="space-y-3" role="list">
            {filteredEditGroups.map((group) => {
              const isOneTime = group.recurrence === RECURRENCE_TYPE.NONE;
              const lifecycle = group.lifecycle;
              const isEffectiveForIcon =
                lifecycle === 'effective' || lifecycle === 'not_started';
              const statusLabel = resolveLifecycleStatusLabel(lifecycle);
              const kindLabel =
                group.configType === TASK_GROUP_CONFIG_TYPE.PICKUP_DROPOFF
                  ? t('task.list.kindPickupDropoff')
                  : t('task.list.kindMedication');
              const canRestoreGroup = restorableTaskIdsByKey.has(group.groupId);
              return (
                <li key={group.groupId} className="overflow-hidden rounded-xl border border-gray-200/80 shadow-sm dark:border-gray-700">
                  <div className="flex items-stretch">
                    <button
                      type="button"
                      onClick={() => handleEditGroupClick(group.groupId, group.configType)}
                      className="min-w-0 flex-1 text-left transition-colors hover:border-primary-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                      aria-label={`${group.displayTitle} (${kindLabel})`}
                    >
                    <div className={`flex w-full items-center gap-3 p-4 pr-1 ${listCornerStatusCardInnerShellClass}`}>
                      <CornerStatusRibbon
                        label={statusLabel}
                        styleClass={taskTemplateLifecycleRibbonClass(lifecycle)}
                      />
                      <div
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${
                        isEffectiveForIcon
                          ? 'bg-primary-50 text-primary-600'
                          : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {isOneTime ? (
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      ) : (
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                          />
                        </svg>
                      )}
                    </div>
                    <div className="min-w-0 flex-1 pr-10">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="truncate font-semibold text-gray-900 dark:text-gray-100">{group.displayTitle}</h3>
                        <span className="inline-flex shrink-0 rounded-full bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-800 dark:bg-sky-950/50 dark:text-sky-200">
                          {kindLabel}
                        </span>
                      </div>
                      <div className="mt-1">
                        <RecurrenceBadge recurrence={group.recurrence} size="sm" />
                      </div>
                    </div>
                      <ListRowChevron />
                    </div>
                    </button>
                    {canRestoreGroup ? (
                      <button
                        type="button"
                        onClick={() => {
                          void handleRestoreArchivedItem(group.groupId);
                        }}
                        disabled={restoreBusyKey === group.groupId}
                        aria-label={t('membership.restoreTask.action')}
                        className="shrink-0 border-l border-gray-200 px-3 text-xs font-semibold text-primary-700 hover:bg-primary-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:opacity-60 dark:border-gray-700 dark:text-primary-300 dark:hover:bg-primary-950/30"
                      >
                        {restoreBusyKey === group.groupId
                          ? t('common.loading')
                          : t('membership.restoreTask.action')}
                      </button>
                    ) : null}
                  </div>
                </li>
              );
            })}
            {filteredEditTasks.map((task) => {
              const isOneTime = task.recurrence === RECURRENCE_TYPE.NONE;
              const lifecycle = getTaskDisplayLifecycle(task, todayYmd);
              const isEffectiveForIcon =
                lifecycle === 'effective' || lifecycle === 'not_started';
              const statusLabel = resolveLifecycleStatusLabel(lifecycle);
              const canRestoreTask = restorableTaskIdsByKey.has(task.id);
              return (
                <li key={task.id} className="overflow-hidden rounded-xl border border-gray-200/80 shadow-sm dark:border-gray-700">
                  <div className="flex items-stretch">
                    <button
                      type="button"
                      onClick={() => handleEditTaskClick(task)}
                      className="min-w-0 flex-1 text-left transition-colors hover:border-primary-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                      aria-label={
                        task.taskKind === TASK_KIND.INVENTORY
                          ? `${task.title} (${t('task.list.kindStockTake')})`
                          : task.title
                      }
                    >
                    <div className={`flex w-full items-center gap-3 p-4 pr-1 ${listCornerStatusCardInnerShellClass}`}>
                      <CornerStatusRibbon
                        label={statusLabel}
                        styleClass={taskTemplateLifecycleRibbonClass(lifecycle)}
                      />
                      <div
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${
                        isEffectiveForIcon
                          ? 'bg-primary-50 text-primary-600'
                          : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {isOneTime ? (
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      ) : (
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                          />
                        </svg>
                      )}
                    </div>
                    <div className="min-w-0 flex-1 pr-10">
                      <div className="flex min-w-0 flex-wrap items-center gap-2">
                        <h3 className="truncate font-semibold text-gray-900 dark:text-gray-100">{task.title}</h3>
                        {task.taskKind === TASK_KIND.INVENTORY ? (
                          <span className="inline-flex shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200">
                            {t('task.list.kindStockTake')}
                          </span>
                        ) : null}
                      </div>
                      <div className="mt-1">
                        <RecurrenceBadge recurrence={task.recurrence} size="sm" />
                      </div>
                    </div>
                      <ListRowChevron />
                    </div>
                    </button>
                    {canRestoreTask ? (
                      <button
                        type="button"
                        onClick={() => {
                          void handleRestoreArchivedItem(task.id);
                        }}
                        disabled={restoreBusyKey === task.id}
                        aria-label={t('membership.restoreTask.action')}
                        className="shrink-0 border-l border-gray-200 px-3 text-xs font-semibold text-primary-700 hover:bg-primary-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:opacity-60 dark:border-gray-700 dark:text-primary-300 dark:hover:bg-primary-950/30"
                      >
                        {restoreBusyKey === task.id
                          ? t('common.loading')
                          : t('membership.restoreTask.action')}
                      </button>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </main>
    </div>
  );
};
